import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import Cookies from "js-cookie";

import { COOKIE_KEYS } from "@/config/storage-keys";
import { clearAuthCookies, refreshAccessToken } from "@/api/token";
import { emitAuthExpired } from "@/lib/auth-event";

/** API 사용 전, ENV 파일을 통해 서버 연동 설정을 해주세요 */
const API_URL = import.meta.env.VITE_API_URL as string;

export type ErrorResponse = { name: string; message: string };

const baseApi = axios.create({
  baseURL: API_URL,
  timeout: 5000,

  headers: {
    "Content-Type": "application/json",
  },
});

/** 개발 환경에서만 실행되논 로깅 함수 */
const logOnDev = (message: string) => {
  if (import.meta.env.MODE === "development") {
    console.log(message);
  }
};

/** API 요청이 실패한 경우 호출되는 함수 */
const onError = (status: number, message: string, data?: ErrorResponse) => {
  const error = { status, message, data };
  throw error;
};

/** request 요청 시, config 객체를 받아와 처리하는 함수 */
const onRequest = (config: AxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  const token = Cookies.get(COOKIE_KEYS.accessToken);
  const { method, url, headers = {} } = config;
  headers.Authorization = token ? `Bearer ${token}` : "";
  logOnDev(`[API REQUEST] ${method?.toUpperCase()} ${url}`);
  return Promise.resolve({ ...config, headers } as InternalAxiosRequestConfig);
};

/** request 요청 시, 발생하는 에러를 처리하는 함수 */
const onErrorRequest = (error: AxiosError<AxiosRequestConfig>) => {
  const { status } = error?.response as AxiosResponse;

  if (error?.config) {
    onError(status, "에러: 요청 실패");
  } else if (error?.request) {
    onError(status, "에러: 응답 없음");
  } else {
    onError(status, "에러:");
  }

  return Promise.reject(error);
};

/** 응답이 성공적으로 수신되었을 때, 호출되는 콜백 함수 */
const onResponse = (response: AxiosResponse): AxiosResponse => {
  const { method, url } = response.config;
  const { status } = response;

  logOnDev(`[API RESPONSE ${status}] ${method?.toUpperCase()} ${url}`);
  return response;
};

/** 응답이 실패한 API 요청에 대한 로깅 및 에러 코드 처리를 담당하는 함수 */
const onErrorResponse = async (error: AxiosError | Error): Promise<never | AxiosResponse> => {
  if (axios.isAxiosError(error)) {
    const { message } = error;
    const { method, url } = error?.config as AxiosRequestConfig;

    if (!error.response) {
      logOnDev(`[API NETWORK_ERROR] ${method?.toUpperCase()} ${url} | ${message}`);
      return Promise.reject(error);
    }

    const { status, statusText, data } = error.response as AxiosResponse<ErrorResponse>;

    logOnDev(`[API ERROR_RESPONSE ${status} | ${statusText} | ${message}] ${method?.toUpperCase()} ${url}`);

    // 401: switch-case의 onError()가 throw하므로 switch 전에 처리
    if (status === 401 || status === 403) {
      // _retry 플래그를 사용 (spread 복사되므로 재시도 config에서도 유지됨)
      const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      if (config._retry) {
        // 401이면 인증 만료 → 로그아웃
        if (status === 401) {
          clearAuthCookies();
          emitAuthExpired();
        }
        // 403이면 권한 없음 → 로그아웃하지 않고 에러만 전파
        return Promise.reject(error);
      }
      try {
        await refreshAccessToken();
        config._retry = true;
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${Cookies.get(COOKIE_KEYS.accessToken)}`;
        return baseApi(config);
      } catch {
        clearAuthCookies();
        emitAuthExpired();
        return Promise.reject(error);
      }
    }

    switch (status) {
      case 400:
        onError(status, "잘못된 요청을 했어요", data);
        break;
      case 404: {
        onError(status, "찾을 수 없는 페이지를 요청했어요", data);
        break;
      }
      case 500: {
        onError(status, "서버 오류가 발생했어요", data);
        break;
      }
      default: {
        onError(status, `기타 에러가 발생했어요 : ${error?.message}`, data);
      }
    }
  } else if (error instanceof Error && error?.name === "TimoutError") {
    logOnDev(`[API TIME_ERROR] ${error?.toString()}`);
    onError(0, "요청 시간이 초과되었어요");
  } else {
    logOnDev(`[API ETC_ERROR] ${error?.toString()}`);
    onError(0, `기타 에러가 발생했어요 : ${error?.toString()}`);
  }

  return Promise.reject(error);
};

/** 인터셉터를 설정 하고, Axios Instance를 반환하는 함수 */
const setInterceptors = (axiosInstance: AxiosInstance): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, onErrorRequest);
  axiosInstance.interceptors.response.use(onResponse, onErrorResponse);

  return axiosInstance;
};

export const api = setInterceptors(baseApi);
